import React, { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Input, Popover, Typography } from 'antd'
import { includes, isEmpty } from 'lodash'
import PropTypes from 'prop-types'

import { GridFormItem } from '../../../core/components/sections'

import {
  StyledButtonWithMargin,
  StyledPaddingBottomRow,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'

import { useUserHasPermissions } from '../../../account/hooks'
import {
  MENU_CURATION_TASK_SCRAPING_REVIEW_ACTION_TYPE,
  MENU_CURATION_TASK_STATUS,
} from '../../constants'
import {
  useRequestMenuCurationTaskScrapingReviewMutation,
  useUpdateMenuCurationTaskScrapingReviewStatusMutation,
} from '../../hooks'

const MenuCurationTaskScrapingReviewButtons = ({
  taskStatus,
  scrapingReviewComment,
  scrapingClarificationComment,
  scrapingReviewerComment,
  isScrapingReviewDone,
}) => {
  const [isRequestReviewPopoverVisible, setIsRequestReviewPopoverVisible] =
    useState(false)
  const [isRejectPopoverVisible, setIsRejectPopoverVisible] = useState(false)
  const [isClarificationPopoverVisible, setIsClarificationPopoverVisible] =
    useState(false)
  const [comment, setComment] = useState('')

  const { id } = useParams()
  const [markAsAwaitingReview, { isLoading: isRequestScrapingReviewLoading }] =
    useRequestMenuCurationTaskScrapingReviewMutation()

  const [
    updateScrapingReviewStatus,
    { isLoading: isUpdateScrapingReviewStatusLoading },
  ] = useUpdateMenuCurationTaskScrapingReviewStatusMutation()

  const hasPermissionToChangeStatus = useUserHasPermissions(
    PERMISSIONS.MENU_CURATION_TASK.UPDATE_SCRAPING_REVIEW_STATUS,
  )

  const hasPermissionToRequestReview = useUserHasPermissions(
    PERMISSIONS.MENU_CURATION_TASK.REQUEST_SCRAPING_REVIEW,
  )

  const shouldShowActionButtons = useMemo(
    () =>
      includes(
        [
          MENU_CURATION_TASK_STATUS.SCRAPING_REVIEW_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.AWAITING_SCRAPING_REVIEW.value,
          MENU_CURATION_TASK_STATUS.NEEDS_SCRAPING_CLARIFICATION.value,
        ],
        taskStatus,
      ),
    [taskStatus],
  )
  const areActionButtonsDisabled = useMemo(
    () =>
      !includes(
        [
          MENU_CURATION_TASK_STATUS.SCRAPING_REVIEW_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.NEEDS_SCRAPING_CLARIFICATION.value,
        ],
        taskStatus,
      ) || !hasPermissionToChangeStatus,
    [hasPermissionToChangeStatus, taskStatus],
  )

  const isRequestClarificationButtonDisabled = useMemo(
    () =>
      areActionButtonsDisabled ||
      MENU_CURATION_TASK_STATUS.NEEDS_SCRAPING_CLARIFICATION.value ===
        taskStatus,
    [areActionButtonsDisabled, taskStatus],
  )

  const isRequestButtonDisabled = useMemo(
    () =>
      MENU_CURATION_TASK_STATUS.RESOLVED.value === taskStatus ||
      !hasPermissionToRequestReview,
    [hasPermissionToRequestReview, taskStatus],
  )

  const resetComment = useCallback(() => {
    setComment('')
  }, [])

  const handleRequestReview = useCallback(() => {
    markAsAwaitingReview(id, comment)
    resetComment()
  }, [comment, id, markAsAwaitingReview, resetComment])

  const handleAccept = useCallback(() => {
    updateScrapingReviewStatus(
      id,
      MENU_CURATION_TASK_SCRAPING_REVIEW_ACTION_TYPE.ACCEPT,
    )
    resetComment()
  }, [id, resetComment, updateScrapingReviewStatus])

  const handleReject = useCallback(() => {
    updateScrapingReviewStatus(
      id,
      MENU_CURATION_TASK_SCRAPING_REVIEW_ACTION_TYPE.REJECT,
      comment,
    )
    resetComment()
  }, [comment, id, resetComment, updateScrapingReviewStatus])

  const handleRquestClarification = useCallback(() => {
    updateScrapingReviewStatus(
      id,
      MENU_CURATION_TASK_SCRAPING_REVIEW_ACTION_TYPE.REQUEST_CLARIFICATION,
      comment,
    )

    resetComment()
  }, [comment, id, resetComment, updateScrapingReviewStatus])

  const handleRequestReviewPopoverVisibleChange = useCallback(
    visible => {
      setIsRequestReviewPopoverVisible(visible)
      resetComment()
    },
    [resetComment],
  )

  const handleRejectPopoverVisibleChange = useCallback(
    visible => {
      setIsRejectPopoverVisible(visible)
      resetComment()
    },
    [resetComment],
  )

  const handleClarificationPopoverVisibleChange = useCallback(
    visible => {
      setIsClarificationPopoverVisible(visible)
      resetComment()
    },
    [resetComment],
  )

  const handleCommentChange = useCallback(
    e => {
      setComment(e.target.value)
    },
    [setComment],
  )

  const requestReviewPopoverContent = useMemo(
    () => (
      <div>
        <Typography.Paragraph>
          Are you sure you want to request a Scraping review for this task?
          <br />
          You will be unassigned and unable to do any further work on this task!
        </Typography.Paragraph>

        <StyledPaddingBottomRow gutter={16}>
          <GridFormItem span={24} label="Comment" labelAbove>
            <Input
              value={comment}
              placeholder="Enter comment..."
              onChange={handleCommentChange}
            />
          </GridFormItem>
        </StyledPaddingBottomRow>

        <Button
          type="primary"
          onClick={handleRequestReview}
          disabled={isRequestButtonDisabled}
          loading={isRequestScrapingReviewLoading}
        >
          Request scraping review
        </Button>
      </div>
    ),
    [
      comment,
      handleCommentChange,
      handleRequestReview,
      isRequestScrapingReviewLoading,
      isRequestButtonDisabled,
    ],
  )

  const rejectPopoverContent = useMemo(
    () => (
      <div>
        <StyledPaddingBottomRow gutter={16}>
          <GridFormItem span={24} label="Comment" labelAbove>
            <Input
              value={comment}
              placeholder="Enter comment..."
              onChange={handleCommentChange}
            />
          </GridFormItem>
        </StyledPaddingBottomRow>

        <Button
          type="primary"
          onClick={handleReject}
          disabled={areActionButtonsDisabled}
          loading={isUpdateScrapingReviewStatusLoading}
        >
          Reject
        </Button>
      </div>
    ),
    [
      areActionButtonsDisabled,
      comment,
      handleCommentChange,
      handleReject,
      isUpdateScrapingReviewStatusLoading,
    ],
  )

  const requestClarificationPopoverContent = useMemo(
    () => (
      <div>
        <StyledPaddingBottomRow gutter={16}>
          <GridFormItem span={24} label="Comment" labelAbove>
            <Input
              value={comment}
              placeholder="Enter comment..."
              onChange={handleCommentChange}
            />
          </GridFormItem>
        </StyledPaddingBottomRow>

        <Button
          type="primary"
          onClick={handleRquestClarification}
          disabled={isRequestClarificationButtonDisabled}
          loading={isUpdateScrapingReviewStatusLoading}
        >
          Request clarification
        </Button>
      </div>
    ),
    [
      isRequestClarificationButtonDisabled,
      comment,
      handleCommentChange,
      handleRquestClarification,
      isUpdateScrapingReviewStatusLoading,
    ],
  )

  if (isScrapingReviewDone) {
    return (
      <>
        <Typography.Paragraph>Scraping review completed.</Typography.Paragraph>
        {!isEmpty(scrapingReviewComment) && (
          <Typography.Paragraph>
            Request comment: {scrapingReviewComment}
          </Typography.Paragraph>
        )}
        {!isEmpty(scrapingReviewerComment) && (
          <Typography.Paragraph>
            Scraper comment:
            <br />
            {scrapingReviewerComment}
          </Typography.Paragraph>
        )}
      </>
    )
  }

  if (shouldShowActionButtons) {
    return (
      <>
        {!isEmpty(scrapingReviewComment) && (
          <Typography.Paragraph>
            Request comment:
            <br />
            {scrapingReviewComment}
          </Typography.Paragraph>
        )}
        {!isEmpty(scrapingClarificationComment) && (
          <Typography.Paragraph>
            Clarification comment:
            <br />
            {scrapingClarificationComment}
          </Typography.Paragraph>
        )}
        <StyledButtonWithMargin
          disabled={areActionButtonsDisabled}
          loading={isUpdateScrapingReviewStatusLoading}
          onClick={handleAccept}
        >
          Accept
        </StyledButtonWithMargin>
        <Popover
          visible={isRejectPopoverVisible}
          onVisibleChange={handleRejectPopoverVisibleChange}
          content={rejectPopoverContent}
          trigger="click"
        >
          <StyledButtonWithMargin
            disabled={areActionButtonsDisabled}
            loading={isUpdateScrapingReviewStatusLoading}
          >
            Reject
          </StyledButtonWithMargin>
        </Popover>
        <Popover
          visible={isClarificationPopoverVisible}
          onVisibleChange={handleClarificationPopoverVisibleChange}
          content={requestClarificationPopoverContent}
          trigger="click"
        >
          <StyledButtonWithMargin
            disabled={isRequestClarificationButtonDisabled}
            loading={isUpdateScrapingReviewStatusLoading}
          >
            Request Clarification
          </StyledButtonWithMargin>
        </Popover>
      </>
    )
  }

  return (
    <Popover
      visible={isRequestReviewPopoverVisible}
      onVisibleChange={handleRequestReviewPopoverVisibleChange}
      content={requestReviewPopoverContent}
      trigger="click"
    >
      <Button
        disabled={isRequestButtonDisabled}
        loading={isRequestScrapingReviewLoading}
      >
        Request scraping review
      </Button>
    </Popover>
  )
}

MenuCurationTaskScrapingReviewButtons.propTypes = {
  taskStatus: PropTypes.string.isRequired,
  scrapingReviewComment: PropTypes.string,
  scrapingReviewerComment: PropTypes.string,
  scrapingClarificationComment: PropTypes.string,
  isScrapingReviewDone: PropTypes.bool.isRequired,
}

MenuCurationTaskScrapingReviewButtons.defaultProps = {
  scrapingReviewComment: '',
  scrapingReviewerComment: '',
  scrapingClarificationComment: '',
}

export default MenuCurationTaskScrapingReviewButtons
